import { find } from "ramda";
import { type ReactNode, useEffect, useState } from "react";
import { Item, Section } from "react-stately";
import { Callout, CalloutIntent, MenuOptionItem, SelectList } from "widgets";

import { ProjectColoredBadge } from "./ProjectColoredWidgets.tsx";
import type { MinimalProject } from "./projects-utils.ts";
import { type ProjectsFetchOptions, useFetchProjects } from "./useFetchProjects.ts";

export interface ProjectsListBox<P extends MinimalProject> extends ProjectsFetchOptions<P> {
  withDescription?: boolean;
  loadingFallback?: ReactNode;
}

export const ProjectsListBox = <P extends MinimalProject>({
  initialSelectedProjectId,
  onSelectionChange,
  ...props
}: ProjectsListBox<P>): ReactNode => {
  const { projectsList, projectsTree, isLoading, error, defaultSelectedProject } = useFetchProjects<P>({
    includeStagedProjects: !props.includeStagedProjects,
    defaultSelection: props.defaultSelection,
  });

  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  useEffect(() => {
    // sync (props.initialSelectedProjectId)
    if (initialSelectedProjectId != null) {
      setSelectedKey(String(initialSelectedProjectId));
    }
  }, [initialSelectedProjectId]);

  useEffect(() => {
    // async (defaultSelectedKey)
    // We don't want to reassign initialSelectedProjectId if defaultSelectedKey changes in the future
    const selection = defaultSelectedProject ?? projectsTree?.[0]?.items?.[0];

    if (initialSelectedProjectId == null && selection != null) {
      setSelectedKey(String(selection.id));
      onSelectionChange?.(selection);
    }
  }, [initialSelectedProjectId, defaultSelectedProject, onSelectionChange, projectsTree]);

  if (error) {
    return (
      <Callout title="Error fetching projects" intent={CalloutIntent.Danger} inline>
        {error.message}
      </Callout>
    );
  }

  if (isLoading) {
    return props.loadingFallback;
  }

  return (
    <SelectList
      noBorder
      items={projectsTree}
      selectedKeys={selectedKey != null ? new Set([String(selectedKey)]) : undefined}
      onSelectionChange={(keys) => {
        if (keys instanceof Set) {
          const key = keys.values().next().value;
          const project = find((project) => project.id === Number(key), projectsList ?? []);
          onSelectionChange?.(project);
          setSelectedKey(String(key));
        }
      }}
      disallowEmptySelection
      selectionMode="single"
    >
      {(section) => (
        <Section items={section.items} key={section.name} title={section.name}>
          {(item) => (
            <Item key={String(item.id)}>
              <MenuOptionItem
                title={item.name}
                description={props.withDescription ? item.description : undefined}
                icon={item.color ? <ProjectColoredBadge project={item} noLink size="menu" /> : "folder"}
                useHover
              />
            </Item>
          )}
        </Section>
      )}
    </SelectList>
  );
};
